import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PermissionEnum, ResponseError, responseErrorsResolver } from '@ui/src';
import {
  useLazyGetFintracSettingsQuery,
  useUpdateFintracReportSettingsMutation,
} from '../../../store/services/reports-settings';
import { FintracSettings, FintracSettingsFormValues } from '../types';

const useFintracSettings = ({
  activePermissions,
  fintracReportSettings,
}: {
  activePermissions?: PermissionEnum[];
  fintracReportSettings?: FintracSettings;
}) => {
  const [globalError, setGlobalError] = useState('');

  const [getFintracSettings, { isLoading, isFetching }] =
    useLazyGetFintracSettingsQuery();
  const [updateFintracReportSettings, { isLoading: requestInProgress }] =
    useUpdateFintracReportSettingsMutation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch,
  } = useForm<FintracSettingsFormValues>();

  const thresholdCurrencyId = watch('thresholdCurrencyId');

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: FintracSettingsFormValues = {
      enabled: values.enabled,
      autoSubmit: values.autoSubmit,
      activitySectorCode:
        !values?.activitySectorCode &&
        fintracReportSettings?.activitySectorCode?.data?.value
          ? null
          : values.activitySectorCode,
      thresholdAmount:
        !values?.thresholdAmount &&
        fintracReportSettings?.thresholdAmount?.data?.value?.toString() &&
        fintracReportSettings?.thresholdAmount?.data?.value?.toString() !==
          'null' &&
        fintracReportSettings?.thresholdAmount?.data?.value?.toString() !==
          'undefined'
          ? null
          : Number(values.thresholdAmount),
      thresholdCurrencyId:
        !values?.thresholdCurrencyId &&
        fintracReportSettings?.thresholdAmount?.data?.value
          ? null
          : values.thresholdCurrencyId,
      aggregationCutoffHours:
        !values?.aggregationCutoffHours &&
        fintracReportSettings?.aggregationCutoffHours?.data?.value?.toString() &&
        fintracReportSettings?.aggregationCutoffHours?.data?.value?.toString() !==
          'null' &&
        fintracReportSettings?.aggregationCutoffHours?.data?.value?.toString() !==
          'undefined'
          ? null
          : Number(values.aggregationCutoffHours),
      baseUrl:
        !values.baseUrl && fintracReportSettings?.baseUrl?.data?.value
          ? null
          : values.baseUrl,
      clientId:
        !values.clientId && fintracReportSettings?.clientId?.data?.value
          ? null
          : values.clientId,
      clientSecret:
        !values.clientSecret && fintracReportSettings?.clientSecret?.data?.value
          ? null
          : values.clientSecret,
    };

    const response = (await updateFintracReportSettings(body)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    if (fintracReportSettings) {
      reset({
        enabled: fintracReportSettings?.enabled.data?.value || false,
        autoSubmit: fintracReportSettings?.autoSubmit?.data?.value || false,
        activitySectorCode:
          fintracReportSettings?.activitySectorCode?.data?.value || '',
        thresholdAmount: fintracReportSettings?.thresholdAmount?.data?.value
          ? fintracReportSettings?.thresholdAmount?.data?.value?.toString()
          : '0',
        thresholdCurrencyId:
          fintracReportSettings?.thresholdCurrencyId?.data?.value || '',
        aggregationCutoffHours: fintracReportSettings?.aggregationCutoffHours
          ?.data?.value
          ? fintracReportSettings?.aggregationCutoffHours?.data?.value?.toString()
          : '0',
        baseUrl: fintracReportSettings?.baseUrl?.data?.value || '',
        clientId: fintracReportSettings?.clientId?.data?.value || '',
        clientSecret: fintracReportSettings?.clientSecret?.data?.value || '',
      });
    }
  }, [fintracReportSettings]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.SETTINGS_REGULATORY_REPORT_FINTRAC_EDIT_ACTION,
      )
    ) {
      getFintracSettings();
    }
  }, [activePermissions]);

  return {
    isLoading,
    isFetching,
    control,
    errors,
    onSubmit,
    requestInProgress,
    globalError,
    thresholdCurrencyId,
  };
};

export default useFintracSettings;
